import { useEffect, useState } from "react";
import { SectionHeader } from "./Features";
import { useParallax } from "../../hooks/useParallax";
import { api } from "../../lib/api";

const LORE_BG =
  "https://images.pexels.com/photos/6227194/pexels-photo-6227194.jpeg?auto=compress&cs=tinysrgb&w=1600";

// Fallback narrative used if the API call fails or returns nothing — keeps
// the page readable even when the DB has no override. Matches the backend
// default in server.py::_default_lore_content().
const DEFAULT_NARRATIVE = [
  {
    style: "drop_cap",
    text: "They called it the Knox Event. A clinical name for the moment the world quietly fell apart. The infected came first — staggering out of hospitals, out of school parking lots, out of the people you'd just had coffee with. Within a week, the National Guard had stopped answering radios.",
  },
  {
    style: "default",
    text: "The first winter was the worst. The dead don't freeze, but the living do. We learned to ration. We learned to bury our own. We learned that the bite isn't the only thing that kills you — sometimes it's the man holding the door open at the end of a long, cold hallway.",
  },
  {
    style: "default",
    text: "Ten years on, the world is smaller and sharper. The roads belong to no one. The cities belong to the dead. We built what we could from what they left behind: walls of scrap, gardens in cracked parking lots, families stitched together from the survivors of other people's tragedies.",
  },
  {
    style: "accent",
    text: "And then someone, somewhere, started broadcasting again.",
  },
];

const TIMELINE = [
  {
    year: "PATIENT 0",
    label: "The Knox Event",
    body:
      "Outbreak begins in the Knox Country exclusion zone. Highways jam. The signal towers go dark within seventy-two hours.",
  },
  {
    year: "YEAR 1",
    label: "The Long Winter",
    body:
      "Power grids collapse statewide. Most who survived the bite die to cold, infection, or one another. Fortified neighborhoods become tombs.",
  },
  {
    year: "YEAR 2-3",
    label: "Scavenger Tides",
    body:
      "Roaming bands pick the bones of suburbs clean. The first crude radio nets are wired together from car batteries and salvaged HAMs.",
  },
  {
    year: "YEAR 4",
    label: "First Settlements",
    body:
      "Walled communities rise around water sources and arable land. Trade routes begin to form along the Ohio River corridor.",
  },
  {
    year: "YEAR 6",
    label: "The Reaping",
    body:
      "A coordinated raid wave from the southern groups destroys three of the largest settlements. The 'old peace' ends overnight.",
  },
  {
    year: "YEAR 8",
    label: "Quiet Years",
    body:
      "Population stabilizes — barely. Children born after the Event come of age. They don't remember the world before. They don't need to.",
  },
  {
    year: "YEAR 10",
    label: "Now.",
    body:
      "You are here. The dead still walk. The radios still hum. The exclusion zone has become a country of its own and someone has just put out a call for survivors. Will the communities in Kentucky rebuild walls and take back their state? Its up to you!",
  },
];

export default function Lore() {
  const bgRef = useParallax(0.3);
  // Only the section header (eyebrow / title / desc) is CMS-driven for now.
  // Narrative + timeline stay as-is (they're on the roadmap to be editable
  // via a structured schema — the previous block-based rewrite lost the
  // two-column layout, so we keep the hardcoded content until we do that
  // right).
  const [header, setHeader] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/content/lore")
      .then((res) => !cancelled && setHeader(res.data?.content || null))
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const eyebrow = header?.eyebrow || "// 02 LORE";
  const title = header?.title || "Ten years of silence.";
  const desc =
    header?.desc ||
    "The world didn't end with a bang. It ended in dripping radiators, empty cereal aisles, and a slowly thinning radio static. Here's how we got here.";
  const narrative = header?.narrative && header.narrative.length ? header.narrative : DEFAULT_NARRATIVE;

  return (
    <section
      id="lore"
      className="relative py-24 sm:py-32 overflow-hidden border-t border-[#283524]"
    >
      <div
        ref={bgRef}
        className="absolute -inset-y-20 inset-x-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url(${LORE_BG})` }}
      />
      <div className="absolute inset-0 bg-[#0D110C]/80" />
      <div className="absolute inset-0 tactical-grid opacity-30" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <SectionHeader eyebrow={eyebrow} title={title} desc={desc} />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Narrative — CMS-driven paragraphs. Style is one of:
              default | drop_cap (large gold first letter) | accent (gold
              flickering line as a mini-heading). */}
          <div className="lg:col-span-5 space-y-6 text-[#E2E4DF]/90 text-base leading-relaxed">
            {narrative.map((p, i) => {
              if (p.style === "drop_cap") {
                return (
                  <p key={i} className="first-letter:font-display first-letter:text-7xl first-letter:text-[#C0A875] first-letter:mr-2 first-letter:float-left first-letter:leading-none">
                    {p.text}
                  </p>
                );
              }
              if (p.style === "accent") {
                return (
                  <p key={i} className="text-[#C0A875] font-display uppercase text-xl pt-4">
                    {p.text}
                  </p>
                );
              }
              return <p key={i}>{p.text}</p>;
            })}
          </div>

          {/* Timeline */}
          <div className="lg:col-span-7">
            <div className="border-l border-[#283524] pl-8 sm:pl-12 ml-2 space-y-12 relative">
              {TIMELINE.map((item, i) => (
                <div key={item.year} data-testid={`timeline-${i}`} className="relative">
                  <span
                    className={`absolute -left-[42px] sm:-left-[54px] top-1 w-3.5 h-3.5 ${
                      i === TIMELINE.length - 1
                        ? "bg-[#C0A875] pulse-dot"
                        : "bg-[#0D110C] border-2 border-[#5C6B51]"
                    }`}
                  />
                  <div className="label-tag text-[#C0A875]">{item.year}</div>
                  <h3 className="mt-1 font-display uppercase text-[#E2E4DF] text-2xl sm:text-3xl">
                    {item.label}
                  </h3>
                  <p className="mt-2 text-[#8D9A87] text-sm leading-relaxed max-w-xl">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
